import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Send, Mail, Linkedin, Twitter, Github, Clock, Video, MessageSquare, AlertCircle, MapPin } from "lucide-react";
import { ScrollReveal } from "@/components/ScrollReveal";
import SEOHead from "@/components/SEOHead";

const projectTypes = [
  "Full-Stack Product Development",
  "AI/ML Integration",
  "FinTech Infrastructure",
  "Trading Platform",
  "Web3 & Blockchain",
  "Cloud & DevOps",
  "API Platform",
  "SaaS Development",
  "Other",
];

const budgetRanges = [
  "Under $10K",
  "$10K - $30K",
  "$30K - $60K",
  "$60K - $150K",
  "$150K+",
];

const hearAbout = [
  "Google Search",
  "LinkedIn",
  "Twitter",
  "Referral",
  "Blog / Article",
  "Other",
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    projectType: "",
    budget: "",
    description: "",
    hearAbout: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    // Netlify Forms expects URL-encoded form data
    const encode = (data) => {
      return Object.keys(data)
        .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
        .join("&");
    };

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", ...formData })
    })
      .then(() => {
        setSubmitted(true);
      })
      .catch((err) => {
        setError("Something went wrong. Please try again or email us directly.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <main data-testid="contact-page">
      <SEOHead
        title="Contact Us — Venury"
        description="Schedule a free consultation or send us your requirements. Let's discuss your next product."
        url="https://venury.tech/contact"
      />
      {/* ==================== HERO ==================== */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0B1116]" />
        <div className="hero-orb-teal" style={{ bottom: "-20%", left: "10%" }} />
        <div className="hero-orb-orange" style={{ top: "10%", right: "5%" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1116]/40 to-[#0B1116]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-16 w-full">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="tag-box font-mono text-sm text-teal-bright uppercase tracking-[0.2em] mb-6"
          >
            Get In Touch
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0, 0, 0.2, 1] }}
            className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white tracking-tight max-w-3xl leading-[1.05]"
          >
            Let's Build Something{" "}
            <span className="gradient-text-teal">Great</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl mt-8"
          >
            Schedule a free consultation or send us your requirements.
          </motion.p>
        </div>
      </section>

      {/* ==================== CONTACT CONTENT ==================== */}
      <section data-testid="contact-form-section" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left - What to expect + Contact Info */}
            <div className="lg:col-span-4">
              <ScrollReveal>
                <div className="sticky top-28">
                  {/* What to expect */}
                  <div className="glass-card rounded-lg p-6 mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Video size={18} className="text-teal-bright" />
                      <h3 className="font-heading text-base font-semibold text-white">What to Expect</h3>
                    </div>
                    <ul className="space-y-3">
                      {[
                        "45-minute video call",
                        "Technical requirements discussion",
                        "Preliminary architecture recommendations",
                        "Ballpark timeline & budget",
                        "No pressure, no obligation",
                      ].map((item, i) => (
                        <li key={i} className="text-sm text-slate-400 flex items-start gap-2">
                          <Clock size={12} className="text-teal-bright mt-1 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-4">
                    <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest">Direct Contact</h4>
                    <a href="mailto:hello@venury.tech" data-testid="contact-email-general" className="flex items-center gap-3 text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                      <Mail size={16} />
                      hello@venury.tech
                    </a>
                    <a href="mailto:sales@venury.tech" data-testid="contact-email-sales" className="flex items-center gap-3 text-sm text-slate-400 hover:text-teal-bright transition-colors duration-200">
                      <Mail size={16} />
                      sales@venury.tech
                    </a>

                    <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest pt-4">Follow Us</h4>
                    <div className="flex items-center gap-4">
                      {/* TODO: Uncomment when profiles are verified/created */}
                      {/* <a href="https://linkedin.com/company/venury" data-testid="contact-linkedin" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                        <Linkedin size={20} />
                      </a>
                      <a href="https://twitter.com/venury_tech" data-testid="contact-twitter" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                        <Twitter size={20} />
                      </a>
                      <a href="https://github.com/venury" data-testid="contact-github" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-bright transition-colors duration-200">
                        <Github size={20} />
                      </a> */}
                    </div>

                    <h4 className="font-mono text-xs text-slate-600 uppercase tracking-widest pt-4">Office</h4>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <MapPin size={16} className="text-teal-bright" />
                      Bangalore, India
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            {/* Right - Form */}
            <div className="lg:col-span-8">
              <ScrollReveal delay={0.15}>
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="form-success-message"
                    className="glass-card rounded-lg p-12 text-center"
                  >
                    <div className="w-16 h-16 rounded-full bg-teal-primary/20 flex items-center justify-center mx-auto mb-6">
                      <MessageSquare size={28} className="text-teal-bright" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-white mb-3">Message Received!</h3>
                    <p className="text-slate-400 text-base leading-relaxed max-w-md mx-auto">
                      Thank you for reaching out. We'll review your requirements and get back to you within 24 hours with a preliminary assessment.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", company: "", phone: "", projectType: "", budget: "", description: "", hearAbout: "" }); }}
                      data-testid="form-reset-button"
                      className="mt-8 font-mono text-sm text-teal-bright hover:text-white transition-colors duration-200"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    data-testid="contact-form"
                    className="glass-card rounded-lg p-8 md:p-10"
                    name="contact"
                    data-netlify="true"
                    netlify-honeypot="bot-field"
                  >
                    <input type="hidden" name="form-name" value="contact" />
                    <p className="hidden">
                      <label>
                        Don’t fill this out if you're human: <input name="bot-field" onChange={handleChange} />
                      </label>
                    </p>
                    <h3 className="font-heading text-xl font-bold text-white mb-8">Tell Us About Your Project</h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Name */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          data-testid="form-name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white placeholder:text-slate-600 rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright"
                          placeholder="Your full name"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          data-testid="form-email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white placeholder:text-slate-600 rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright"
                          placeholder="you@company.com"
                        />
                      </div>

                      {/* Company */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          data-testid="form-company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white placeholder:text-slate-600 rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright"
                          placeholder="Company name"
                        />
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Phone (optional)
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          data-testid="form-phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white placeholder:text-slate-600 rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright"
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>

                      {/* Project Type */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Project Type *
                        </label>
                        <select
                          name="projectType"
                          data-testid="form-project-type"
                          value={formData.projectType}
                          onChange={handleChange}
                          required
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright appearance-none"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: "right 0.75rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                        >
                          <option value="" className="bg-navy-base">Select project type</option>
                          {projectTypes.map((t) => (
                            <option key={t} value={t} className="bg-navy-base">{t}</option>
                          ))}
                        </select>
                      </div>

                      {/* Budget */}
                      <div>
                        <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                          Budget Range *
                        </label>
                        <select
                          name="budget"
                          data-testid="form-budget"
                          value={formData.budget}
                          onChange={handleChange}
                          required
                          className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright appearance-none"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: "right 0.75rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                        >
                          <option value="" className="bg-navy-base">Select budget range</option>
                          {budgetRanges.map((b) => (
                            <option key={b} value={b} className="bg-navy-base">{b}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-6">
                      <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                        Project Description *
                      </label>
                      <textarea
                        name="description"
                        data-testid="form-description"
                        value={formData.description}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white placeholder:text-slate-600 rounded-md px-4 py-3 text-sm outline-none resize-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright"
                        placeholder="Tell us about your project, requirements, and timeline expectations..."
                      />
                    </div>

                    {/* How did you hear */}
                    <div className="mt-6">
                      <label className="font-mono text-xs text-slate-600 uppercase tracking-widest block mb-2">
                        How did you hear about us?
                      </label>
                      <select
                        name="hearAbout"
                        data-testid="form-hear-about"
                        value={formData.hearAbout}
                        onChange={handleChange}
                        className="w-full bg-navy-base/50 border border-slate-700 focus:border-teal-bright text-white rounded-md px-4 py-3 text-sm outline-none transition-colors duration-200 focus:ring-1 focus:ring-teal-bright appearance-none"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: "right 0.75rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                      >
                        <option value="" className="bg-navy-base">Select an option</option>
                        {hearAbout.map((h) => (
                          <option key={h} value={h} className="bg-navy-base">{h}</option>
                        ))}
                      </select>
                    </div>

                    <button
                      type="submit"
                      data-testid="form-submit-button"
                      disabled={submitting}
                      className="mt-8 inline-flex items-center gap-3 bg-teal-primary hover:bg-teal-dark text-white font-bold text-base px-8 py-4 rounded w-full justify-center transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send size={16} />
                      <span>{submitting ? "Sending..." : "Submit"}</span>
                    </button>

                    {error && (
                      <div data-testid="form-error-message" className="mt-4 flex items-center gap-2 text-red-400 text-sm">
                        <AlertCircle size={14} />
                        {error}
                      </div>
                    )}
                  </form>
                )}
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
